/**
 * Resolves the one thing the detail screen must know before it can render:
 * which school this is, and whether it is a head office or a standalone school.
 *
 * There is no per-school endpoint. The health-stat endpoint is used first
 * because it returns every school in a single response — id, name and
 * `is_multi_campus` — so a direct visit (or a refresh) usually resolves in one
 * request and the dashboards start fetching that much sooner.
 *
 * health-stat leaves some schools out, though — paused ones among them (school
 * 930 on staging) — while the Reading by school table still lists them. For
 * those the school's name comes from its own plan history
 * (`school/get-plans/{id}`), and the reader-school list, searched by that name,
 * gives the row with this id.
 */

import { useEffect, useState } from "react";
import { getPlanHistory } from "shared/services/schoolMembershipService";
import {
  getHealthStatService,
  getReaderSchoolsService,
} from "shared/services/schoolStatService";
import type { HealthStat, ReaderSchools } from "../schoolAnalytics/adapters";

type HealthSchool = HealthStat["healthy"]["schools"][0];

export interface SchoolSummary {
  school_id: number;
  name: string;
  /** 1 = head office (campus analytics), 0 = standalone school (class analytics). */
  is_multi_campus: number;
  /** healthy | slipping | dead — the bucket the school was found in. */
  health_band: string;
}

const BANDS: (keyof HealthStat)[] = ["healthy", "slipping", "dead"];

/** The reader-school list's largest page. */
const SEARCH_PAGE_SIZE = 50;

const findInHealthStat = async (id: number) => {
  const response = await getHealthStatService();
  const data = response?.data?.data as HealthStat | undefined;

  for (const band of BANDS) {
    const match = (data?.[band]?.schools ?? []).find(
      (row: HealthSchool) => row.school_id === id
    );
    if (match) {
      return {
        school_id: match.school_id,
        name: match.name,
        is_multi_campus: Number(match.is_multi_campus) || 0,
        health_band: band,
      };
    }
  }
  return undefined;
};

const findInReaderSchools = async (id: number) => {
  const plans = await getPlanHistory(id, { page: 1, per_page: 1 });
  const name = plans?.data?.data?.user?.school_name;
  if (!name) return undefined;

  const response = await getReaderSchoolsService(
    undefined,
    undefined,
    1,
    SEARCH_PAGE_SIZE,
    undefined,
    name
  );
  const data = response?.data?.data as ReaderSchools | undefined;
  const match = (data?.schools ?? []).find((row) => row.school_id === id);
  if (!match) return undefined;

  return {
    school_id: match.school_id,
    name: match.head_office?.name || name,
    is_multi_campus: Number(match.is_multi_campus) || 0,
    health_band: match.health_band,
  };
};

function useSchoolDetail(id: number) {
  const [school, setSchool] = useState<SchoolSummary | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    if (!Number.isFinite(id)) {
      setSchool(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSchool(undefined);

    findInHealthStat(id)
      .catch(() => undefined)
      .then((found) => found ?? findInReaderSchools(id))
      .then((found) => {
        if (!cancelled && found) setSchool(found);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return { school, loading };
}

export default useSchoolDetail;
